import { useCallback } from "react";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import ErrorSnackbarContent from "components/error-snackbar-content";
import { selectCurrentUser } from "features/auth/auth-slice";
import { useCreateFeatureSubscriptionMutation } from "services/paywall";
import type { PaywallFeature } from "services/types/paywall.type";
import { getCurrentRoutePath } from "utils/common";

import {
  getPaywallConflictMessage,
  getPaywallSupportError
} from "./paywall-purchase-error.util";

/**
 * What the CALLER still has to present. Anything not returned as `needs-support`,
 * `trial-started` or `conflict` has already been handled here (a redirect, or the error
 * snackbar). `trial-started` (SN-19868): with no usable Stripe customer the backend started the
 * trial without a card instead of a checkout. `conflict`: the practice's billing changed during
 * the request; nothing was charged and trying again works.
 */
export type PaywallPurchaseResult =
  | { outcome: "redirecting" }
  | {
      outcome: "trial-started";
      trialEnd: string | null;
      trialDays: number | null;
    }
  | { outcome: "needs-support"; message: string; subscriptionId?: number }
  | { outcome: "conflict"; message: string }
  | { outcome: "failed" };

export default function usePurchasePaywallFeature() {
  const [
    createFeatureSubscription,
    { isLoading: isCreatingFeatureSubscription }
  ] = useCreateFeatureSubscriptionMutation();
  const currentUser = useAppSelector(selectCurrentUser);
  const dispatch = useAppDispatch();

  const purchasePaywallFeature = useCallback(
    async (
      feature: PaywallFeature,
      number_of_licenses: number,
      user_id?: number
    ): Promise<PaywallPurchaseResult> => {
      const response = await createFeatureSubscription({
        feature_id: Number(feature?.id),
        user_id: !user_id ? Number(currentUser?.id) : user_id,
        number_of_licenses,
        redirect_params: getCurrentRoutePath()
      });

      const data = "data" in response ? response.data : undefined;

      // No usable Stripe customer: the backend started the trial without a card instead.
      if (
        data &&
        "subscription_state" in data &&
        data.subscription_state === "active_trial"
      ) {
        return {
          outcome: "trial-started",
          trialEnd: data.trial_end,
          trialDays: data.trial_days
        };
      }

      if (data && "checkout_url" in data && data.checkout_url) {
        window.location.href = data.checkout_url;
        return { outcome: "redirecting" };
      }

      if ("error" in response) {
        // A 400 from this endpoint means the practice cannot subscribe for a reason only
        // Suno support can clear. Handed back rather than shown here: the snackbar has
        // never actually surfaced these sentences (`ErrorSnackbarContent` reads `message` /
        // `detail`, not a bare `error` key), and only the caller knows which layer it is
        // rendering on.
        const supportError = getPaywallSupportError(response.error);
        if (supportError) {
          return { outcome: "needs-support", ...supportError };
        }
        // The snackbar would read "An Error has Occurred": it does not read the `error` key.
        const conflict = getPaywallConflictMessage(response.error);
        if (conflict) return { outcome: "conflict", message: conflict };

        dispatch(setCustomMessage(<ErrorSnackbarContent response={response} />));
        dispatch(setOpen(true));
        return { outcome: "failed" };
      }

      // A 200 with no checkout_url used to match neither branch and silently do nothing.
      dispatch(setCustomMessage(<ErrorSnackbarContent response={response} />));
      dispatch(setOpen(true));
      return { outcome: "failed" };
    },
    [createFeatureSubscription, currentUser?.id, dispatch]
  );

  return {
    purchasePaywallFeature,
    isCreatingFeatureSubscription
  };
}
